import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { BodyWrapper, Header, Loader, MainWrapper, Message } from '@components';
import { ChevronRightIcon } from '@svgs';
import { businessQuery, siteMutation, themeMutation } from '@queries';

import { ProjectBreadcrumbs } from '../components/ProjectBreadcrumbs/ProjectBreadcrumbs.jsx';
import ProjectSidebar from '../components/ProjectSidebar/ProjectSidebar.jsx';

import { WebsiteForm } from './WebsiteForm/WebsiteForm.jsx';
import { errorsFromMutate } from '@Forms/utils.js';
import { useState, useEffect, useRef } from 'react';
export const AddWebsite = () => {
  const { t, i18n } = useTranslation();
  const { businessID } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState(null);
  const errorMessageRef = useRef(null);
  
  // scroll to error message when it's set
  useEffect(() => {
    if (errorMessage && errorMessageRef.current) {
      errorMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [errorMessage]);

  // Fetches business
  const { data: businessData, isPending: businessIsPending } = useQuery(
    businessQuery(businessID),
  );

  // save site
  const createTheme = useMutation(themeMutation(null));
  const createSite = useMutation(
    siteMutation(null, {
      onMutate: (data) => {
        setErrorMessage(null);
      },
      onSuccess: (data) => {
        // invalidate the query so it is fetched again
        queryClient.invalidateQueries({ queryKey: ['sites', 'list'] });
        // navigate to the editing view
        navigate(`/dashboard/sites/${data.id}/pages`);
      },
      onError: (error) => {
        const errorsList = errorsFromMutate(error);
        const allError = errorsList.find(({ field }) => field === '__all__');
        if(!!allError) {
          setErrorMessage(allError.error.message);
        }
      },
    }),
  );

  const siteData = businessData
    ? {
        formatted_url: businessData.primary_domain
          ? `https://www.${businessData.primary_domain}/`
          : '',
        title: `${businessData.business_name} Primary Website`,
      }
    : {};

  return (
    <>
      <Header />
      <BodyWrapper>
        <ProjectSidebar businessID={businessID} businessData={businessData} />
        <MainWrapper>
          <ProjectBreadcrumbs businessData={businessData}>
            <li className="sb-flex">
              <Link to={`/dashboard/projects/${businessID}/sites`}>
                {t('Websites')}
              </Link>
              <ChevronRightIcon />
            </li>
            <li className="sb-flex">
              <span>{t('Add')}</span>
            </li>
          </ProjectBreadcrumbs>
          <div className="sb-websites">
            <div className="sb-manage-content-head">
              <h1>{t('Add')}</h1>
            </div>
            {errorMessage && (
              <div ref={errorMessageRef}>
                <Message closable={true} messageStyle="error" title={'Something Went Wrong'} isFormMessage={false} message={errorMessage}/>
              </div>
            )}
            {businessIsPending && (
              <Loader className="sb-loader sb-loader-large" />
            )}
            {businessData && (
              <WebsiteForm
                businessData={businessData}
                createSite={createSite}
                createTheme={createTheme}
                disabled={createSite.isPending || createTheme.isPending}
                siteData={siteData}
                onCancel={() => {
                  navigate(`/dashboard/projects/${businessID}/sites`);
                }}

              />
            )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};
